<script lang="ts">
  interface Props {
    csrfToken?: string;
    inviteToken?: string;
    returnTo?: string;
  }

  let { csrfToken = "", inviteToken = "", returnTo = "" }: Props = $props();

  let email = $state("");
  let password = $state("");
  let rememberMe = $state(false);
  let loading = $state(false);
  let errorMessage = $state("");

  async function handleSubmit(e: SubmitEvent) {
    e.preventDefault();
    loading = true;
    errorMessage = "";

    try {
      const formData = new FormData();
      formData.append("user[login]", email);
      formData.append("user[password]", password);
      if (rememberMe) {
        formData.append("user[remember_me]", "on");
      }
      formData.append("authenticity_token", csrfToken);

      const searchParams = new URLSearchParams();
      if (inviteToken) searchParams.set("invite", inviteToken);
      if (returnTo) searchParams.set("return_to", returnTo);
      const loginUrl = searchParams.toString() ? `/login?${searchParams.toString()}` : "/login";

      const response = await fetch(loginUrl, {
        method: "POST",
        credentials: "same-origin",
        headers: { Accept: "application/json" },
        body: formData,
      });

      if (response.ok) {
        let redirectTo = "/customer";
        const contentType = response.headers.get("content-type") || "";

        if (contentType.includes("application/json")) {
          const data = await response.json();
          if (data?.redirect_to) redirectTo = data.redirect_to;
        }

        window.location.href = redirectTo;
      } else if (response.status === 422) {
        errorMessage = "Invalid email or password. Please try again.";
      } else {
        errorMessage = "An error occurred. Please try again.";
      }
    } catch (error) {
      console.error("Login error:", error);
      errorMessage = "An error occurred. Please try again.";
    } finally {
      loading = false;
    }
  }
</script>

<div class="space-y-6">
  <div class="text-center">
    <h1 class="text-4xl font-bold text-foreground">Welcome back</h1>
    <p class="mt-2 text-base text-muted-foreground">Sign in to your DataTurk.ai account</p>
  </div>

  {#if errorMessage}
    <div class="rounded-md border border-destructive/20 bg-destructive/10 p-4">
      <p class="text-base text-destructive">{errorMessage}</p>
    </div>
  {/if}

  <form onsubmit={handleSubmit} class="space-y-4">
    <div>
      <label for="email" class="block text-base font-medium text-foreground">
        Email
      </label>
      <input
        id="email"
        type="email"
        placeholder="your@email.com"
        bind:value={email}
        required
        disabled={loading}
        class="mt-1 w-full rounded-md border border-border bg-background px-3 py-3 text-base shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:opacity-50"
      />
    </div>

    <div>
      <label for="password" class="block text-base font-medium text-foreground">
        Password
      </label>
      <input
        id="password"
        type="password"
        placeholder="••••••••"
        bind:value={password}
        required
        disabled={loading}
        class="mt-1 w-full rounded-md border border-border bg-background px-3 py-3 text-base shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:opacity-50"
      />
      <div class="mt-2 text-right">
        <a href="/forgot-password" class="text-base font-medium text-primary hover:text-primary/80">
          Forgot password?
        </a>
      </div>
    </div>

    <div class="flex items-center">
      <input
        id="remember-me"
        type="checkbox"
        bind:checked={rememberMe}
        disabled={loading}
        class="h-4 w-4 rounded border-border"
      />
      <label
        for="remember-me"
        class="ml-2 cursor-pointer text-base text-foreground"
      >
        Remember me
      </label>
    </div>

    <button
      type="submit"
      disabled={loading}
      class="w-full rounded-md bg-primary px-4 py-3 text-base font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:opacity-50"
    >
      {loading ? "Signing in..." : "Sign in"}
    </button>
  </form>

  <p class="text-center text-base text-muted-foreground">
    Don't have an account?
    <a href="/signup" class="font-medium text-primary hover:text-primary/80">
      Sign up
    </a>
  </p>
</div>

